import { useEffect } from "react";

import { cn } from "@/lib/utils";

/** One ripple, as `useTouchRipple` tracks it. */
export type Ripple = {
  key: number;
  /** Origin, in the pressed surface's own coordinates. */
  x: number;
  y: number;
  /** Diameter, big enough that the circle covers the surface's furthest corner. */
  size: number;
  leaving: boolean;
  /** MUI's focus ripple: centred, and breathing until it is retired. */
  pulsate?: boolean;
};

const DURATION_MS = 550;

// Read at exit rather than subscribed to: the only thing the preference changes
// here is how long a retiring ripple must stay mounted, which is decided once —
// when it starts retiring.
function prefersReducedMotion() {
  // Guarded as `mui-collapse.tsx` is: some webviews expose no `matchMedia`.
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// A single ripple. Split from the hook the way MUI splits `Ripple.js` from
// `TouchRipple.js`, and for the same reason: each ripple owns the timing of its
// own exit, so it has to be a component with its own effect.
export function RippleCircle({
  ripple,
  onExited,
}: {
  ripple: Ripple;
  onExited: (key: number) => void;
}) {
  const { key, x, y, size, leaving, pulsate = false } = ripple;

  // The fade-out is a CSS animation, so nothing tells React when it has
  // finished; the node is held for exactly as long as the animation lasts and
  // then dropped. Under reduced motion there is no animation to wait for.
  useEffect(() => {
    if (!leaving) {
      return;
    }
    const timer = window.setTimeout(() => onExited(key), prefersReducedMotion() ? 0 : DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, key, onExited]);

  return (
    <span
      data-slot="ripple"
      data-pulsate={pulsate ? "" : undefined}
      className={cn("ripple", pulsate && "ripple-pulsate")}
      // Geometry, not style: size and origin are computed per press, so they
      // cannot be expressed as classes.
      style={{ width: size, height: size, top: -(size / 2) + y, left: -(size / 2) + x }}
    >
      <span
        className={cn(
          "ripple-body",
          leaving && "ripple-body-leaving",
          pulsate && "ripple-body-pulsate",
        )}
      />
    </span>
  );
}
